// pages/VideosPageHome.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Skeleton,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
// import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import { getVideos } from "../utils/generalSubjectApi";
import { getYouTubeId } from "../utils/youtube";
import {
  BookRounded,
  MovieRounded,
  MusicNoteRounded,
  SportsEsportsRounded,
  StarRounded,
} from "@mui/icons-material";

const glass = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.75)",
  border: "1px solid rgba(255,255,255,0.92)",
  boxShadow:
    "0 8px 32px rgba(15,23,42,0.07), inset 0 1px 0 rgba(255,255,255,0.95)",
};

// Premium color palette
const palette = [
  {
    color: "#E50914",
    bg: "rgba(229,9,20,0.1)",
    border: "1px solid rgba(229,9,20,0.3)",
    icon: <MovieRounded />,
  },
  {
    color: "#1DB954",
    bg: "rgba(29,185,84,0.1)",
    border: "1px solid rgba(29,185,84,0.3)",
    icon: <MusicNoteRounded />,
  },
  {
    color: "#FF9800",
    bg: "rgba(255,152,0,0.1)",
    border: "1px solid rgba(255,152,0,0.3)",
    icon: <BookRounded />,
  },
  {
    color: "#3F51B5",
    bg: "rgba(63,81,181,0.1)",
    border: "1px solid rgba(63,81,181,0.3)",
    icon: <SportsEsportsRounded />,
  },
  {
    color: "#9C27B0",
    bg: "rgba(156,39,176,0.1)",
    border: "1px solid rgba(156,39,176,0.3)",
    icon: <StarRounded />,
  },
];

/* ── single video card (YouTube card style) ── */
const VideoCard = ({
  vid,
  index,
  navigate,
  subjectId,
  examId,
}) => {
  const youtubeId = getYouTubeId(vid.video_url);
  const isCloudinary = vid.video_url && !youtubeId;
  const getCloudinaryThumbnail = (url) => {
    if (!url) return null;

    return url
      .replace("/video/upload/", "/video/upload/so_1,q_auto,f_auto/")
      .replace(/\.\w+$/, ".jpg");
  };

  const thumbnail = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
    : isCloudinary
    ? getCloudinaryThumbnail(vid.video_url)
    : null;

  const hqThumbnail = youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`
    : isCloudinary
    ? getCloudinaryThumbnail(vid.video_url) // Cloudinary already high quality
    : null;
  const [imgSrc, setImgSrc] = useState(hqThumbnail || thumbnail);
  const [hovered, setHovered] = useState(false);
  const subjectName = vid.subject_name || vid.subject || "";
  const categoryName = vid.category_name || "";
  const studentName = vid.student_name || "";
  const year = vid.year || "";

  const getCategoryStyle = (name) => {
    const index =
      Math.abs(
        name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
      ) % palette.length;
    return palette[index];
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * 0.055,
        ease: [0.22, 1, 0.36, 1],
      }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      style={{ cursor: "pointer", height: "100%" }}
      onClick={() => {
        navigate(`/videos/${vid.id}`, {
          state: {
            subjectId,
            examId,
            isAdmin: false,
          },
        });
      }}
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* thumbnail wrapper */}
        <Box
          sx={{
            position: "relative",
            borderRadius: "18px",
            overflow: "hidden",
            aspectRatio: "16/9",
            background: "linear-gradient(135deg,#1e1b4b,#312e81)",
            boxShadow: hovered
              ? "0 20px 48px rgba(99,102,241,0.25)"
              : "0 6px 20px rgba(15,23,42,0.12)",
            transition: "box-shadow 0.35s ease",
          }}
        >
          {imgSrc ? (
            <Box
              component="img"
              src={imgSrc}
              alt={vid.title}
              onError={() => {
                if (imgSrc === hqThumbnail && thumbnail) setImgSrc(thumbnail);
                else setImgSrc(null);
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transition: "transform 0.5s cubic-bezier(0.22,1,0.36,1)",
                transform: hovered ? "scale(1.07)" : "scale(1)",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VideoLibraryRoundedIcon
                sx={{ color: "rgba(255,255,255,0.2)", fontSize: 48 }}
              />
            </Box>
          )}

          {/* play button overlay */}
          <AnimatePresence>
            {hovered && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(0,0,0,0.32)",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.95)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                  }}
                >
                  <PlayArrowRoundedIcon
                    sx={{ color: "#0f172a", fontSize: 30, ml: "3px" }}
                  />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* duration badge placeholder */}
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              right: 8,
              px: 1,
              py: 0.3,
              borderRadius: "6px",
              background: "rgba(0,0,0,0.75)",
              backdropFilter: "blur(8px)",
            }}
          >
            <Typography
              sx={{ color: "#fff", fontSize: "0.7rem", fontWeight: 700 }}
            >
              ▶ Watch
            </Typography>
          </Box>
        </Box>

        {/* meta */}
        <Box
          sx={{ pt: 1.8, flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Typography
            sx={{
              fontFamily: "'Clash Display', sans-serif",
              fontWeight: 700,
              fontSize: "0.97rem",
              color: hovered ? "#6366f1" : "#0f172a",
              lineHeight: 1.4,
              mb: 0.8,
              transition: "color 0.2s",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textTransform: "capitalize",
            }}
          >
            {vid.title}
          </Typography>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              px: 1.4,
              py: 0.5,
              borderRadius: "10px",
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              background: vid.watched
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              backdropFilter: "blur(6px)",
              border: "1px solid rgba(255,255,255,0.2)",
              transition: "all 0.25s ease",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
              },
            }}
          >
            {vid.watched ? "Watched" : "New"}
          </Box>

          {subjectName && (
            <Chip
              label={subjectName}
              size="small"
              sx={{
                background: "rgba(99,102,241,0.09)",
                color: "#6366f1",
                fontWeight: 600,
                fontSize: "0.68rem",
                border: "1px solid rgba(99,102,241,0.18)",
                borderRadius: "8px",
                height: 22,
                width: "fit-content",
                textTransform: "capitalize",
              }}
            />
          )}

          {categoryName &&
            (() => {
              const style = getCategoryStyle(categoryName);
              return (
                <Chip
                  label={categoryName}
                  size="small"
                  icon={React.cloneElement(style.icon, {
                    style: { fontSize: 12, color: style.color },
                  })}
                  sx={{
                    mb: 1.2,
                    height: 22,
                    width: "fit-content",
                    fontFamily: "'DM Sans',sans-serif",
                    fontWeight: 600,
                    marginTop: "10px",
                    fontSize: "0.7rem",
                    color: style.color,
                    borderRadius: "8px",
                    background: style.bg,
                    border: style.border,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                    backdropFilter: "blur(6px)",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                    },
                    "& .MuiChip-label": { px: 0.8 },
                    "& .MuiChip-icon": { ml: 0.8 },
                  }}
                />
                
              );
            })()}
            {studentName && (
              <Typography
                sx={{
                  mt: 0.8,
                  color: "#64748b",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Teacher: {studentName}
              </Typography>
            )}

            {year && (
              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Year: {year}
              </Typography>
            )}
        </Box>
      </Box>
    </motion.div>
  );
};

/* ── main page ── */
export default function VideosPageHome() {
  const { subjectId, examId } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const location = useLocation();

  useEffect(() => {
    setLoading(true);

    getVideos(subjectId, examId)
      .then((d) => {
        setVideos(d || []);
      })
      .catch((error) => {
        console.error(error);
        setVideos([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [subjectId, examId, location.key]);

  const years = [
    ...new Set(
      videos
        .map((video) => video.year)
        .filter((year) => year !== null && year !== undefined && year !== "")
        .map(String)
    ),
  ].sort((a, b) => Number(b) - Number(a));

  const categories = [
    ...new Set(
      videos
        .map((video) => video.category_name)
        .filter(Boolean)
        .map((category) => String(category).trim())
    ),
  ].sort((a, b) => a.localeCompare(b));

  const filtered = videos
    .filter((video) => {
      const title = String(video.title || "").toLowerCase();
      const category = String(video.category_name || "").trim();
      const year = String(video.year ?? "");

      const matchesTitle = title.includes(search.trim().toLowerCase());

      const matchesYear =
        !yearFilter || year === yearFilter;

      const matchesCategory =
        !categoryFilter || category === categoryFilter;

      return matchesTitle && matchesYear && matchesCategory;
    })
    .sort((a, b) => {
      // Show unwatched videos first.
      if (a.watched === b.watched) return 0;
      return a.watched ? 1 : -1;
    });
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg,#f8faff 0%,#f1f5ff 50%,#faf8ff 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ambient */}
      <Box
        sx={{
          position: "fixed",
          top: -160,
          right: -100,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(99,102,241,0.1) 0%,transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          bottom: -120,
          left: -80,
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(circle,rgba(16,185,129,0.08) 0%,transparent 70%)",
          filter: "blur(50px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(99,102,241,0.05) 1px,transparent 1px)",
          backgroundSize: "26px 26px",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse 80% 80% at 50% 50%,black 50%,transparent 100%)",
        }}
      />

      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 2.5, sm: 4, md: 6 },
          pt: { xs: 4, md: 6 },
          pb: 12,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* back */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 5,
              px: 2.5,
              py: 1.2,
              borderRadius: "14px",
              cursor: "pointer",
              ...glass,
              fontWeight: 600,
              color: "#475569",
              fontSize: "0.88rem",
              "&:hover": { color: "#6366f1" },
              transition: "color 0.2s",
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
            Back
          </Box>
        </motion.div>

        {/* header row */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            gap={3}
            mb={5}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Clash Display', sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: "2rem", md: "2.6rem" },
                  color: "#0f172a",
                  letterSpacing: "-0.05em",
                  lineHeight: 1.1,
                }}
              >
                Video Lessons
              </Typography>
              {!loading && (
                <Typography sx={{ color: "#64748b", mt: 0.5 }}>
                  {filtered.length} video{filtered.length !== 1 ? "s" : ""}{" "}
                  available
                </Typography>
              )}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: "100%", maxWidth: 850 }}
            >
              {(search || yearFilter || categoryFilter) && (
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      label="Clear all filters"
                      onClick={() => {
                        setSearch("");
                        setYearFilter("");
                        setCategoryFilter("");
                      }}
                      onDelete={() => {
                        setSearch("");
                        setYearFilter("");
                        setCategoryFilter("");
                      }}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 600,
                        color: "#6366f1",
                        background: "rgba(99,102,241,0.08)",
                      }}
                    />
                  </Box>
                )}
              {/* Search by title */}
              <TextField
                placeholder="Search video title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                sx={{
                  flex: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.82)",
                    boxShadow: "0 4px 16px rgba(99,102,241,0.09)",
                    "& fieldset": {
                      borderColor: "rgba(99,102,241,0.18)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(99,102,241,0.4)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#6366f1",
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Filter by year */}
              <FormControl
                sx={{
                  flex: 1,
                  minWidth: { xs: "100%", sm: 135 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.82)",
                  },
                }}
              >
                <InputLabel id="video-year-label">Year</InputLabel>

                <Select
                  labelId="video-year-label"
                  value={yearFilter}
                  label="Year"
                  onChange={(e) => setYearFilter(e.target.value)}
                >
                  <MenuItem value="">All years</MenuItem>

                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filter by category */}
              <FormControl
                sx={{
                  flex: 1,
                  minWidth: { xs: "100%", sm: 165 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    background: "rgba(255,255,255,0.82)",
                  },
                }}
              >
                <InputLabel id="video-category-label">
                  Category
                </InputLabel>

                <Select
                  labelId="video-category-label"
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <MenuItem value="">All categories</MenuItem>

                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </motion.div>

        {/* grid */}
        <AnimatePresence mode="wait">
          {loading ? (
            <Grid container spacing={3}>
              {[...Array(8)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  <Box>
                    <Skeleton
                      variant="rounded"
                      sx={{ aspectRatio: "16/9", borderRadius: "18px", mb: 2 }}
                    />
                    <Skeleton variant="text" width="90%" height={22} />
                    <Skeleton variant="text" width="55%" height={18} />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : filtered.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ textAlign: "center", paddingTop: 80 }}
            >
              <VideoLibraryRoundedIcon
                sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }}
              />
              <Typography
                sx={{
                  fontFamily: "'Clash Display', sans-serif",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#0f172a",
                  mb: 1,
                }}
              >
                {search ? "No results" : "No videos yet"}
              </Typography>
              <Typography sx={{ color: "#64748b" }}>
                {search
                  ? `Nothing matches "${search}"`
                  : "Check back soon for new content"}
              </Typography>
            </motion.div>
          ) : (
            <Grid container spacing={{ xs: 3, md: 3.5 }}>
              {filtered.map((vid, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={vid.id}>
                  <VideoCard
                    vid={vid}
                    index={i}
                    navigate={navigate}
                    subjectId={subjectId}
                    examId={examId}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
